import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PrimeNGModule } from '../../primeng.module';
import { AddressService } from '../../services/address/address.service';
import { AuthService } from '../../services/auth/auth.service';
import { BudgetService } from '../../services/budget/budget.service';
import { CartService } from '../../services/cart/cart.service';
import { FreightService } from '../../services/freight/freight.service';
import { GoogleAnalyticsService } from '../../services/google/google-analytics.service';
import { LoaderService } from '../../services/loader/loader.service';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { MercadoPagoService } from '../../services/mercado-pago/mercado-pago.service';
import { ProductsService } from '../../services/product/products.service';
import { SessionStorageService } from '../../services/session-storage/session-storage.service';
import { ToastService } from '../../services/toast/toast.service';
import { TransactionsService } from '../../services/transactions/transactions.service';
import { UserService } from '../../services/user/user.service';
import { UtmService } from '../../services/utm/utm.service';
import {
  AddressComponent,
  AddressDisable,
  AddressError,
} from '../../shared/components/address/address.component';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import {
  CardInputError,
  CreditCardInputComponent,
} from '../../shared/components/credit-card-input/credit-card-input.component';
import { FreightQuotesDesktopComponent } from '../../shared/components/freight-quotes-desktop/freight-quotes-desktop.component';
import { ModalPixComponent } from '../../shared/components/modals/modal-pix/modal-pix.component';
import { BlindsOptionsEnum } from '../../shared/models/app/info-enum';
import Budget from '../../shared/models/budget/budget';
import { CartItem } from '../../shared/models/cart/cart';
import CartCheckoutDetails from '../../shared/models/cart/cart-checkout-details';
import Checkout from '../../shared/models/checkout/checkout';
import CheckoutBudget from '../../shared/models/checkout/checkout-budget';
import CheckoutPayment from '../../shared/models/checkout/checkout-payment';
import CreditCart from '../../shared/models/checkout/credit-cart';
import PaymentSender from '../../shared/models/checkout/payment-sender';
import { Pix } from '../../shared/models/checkout/pix';
import CouponDiscount from '../../shared/models/coupons/coupon-discount';
import CalculateShipping from '../../shared/models/freight/calculate-shipping';
import Carrying from '../../shared/models/freight/carrying';
import Freight from '../../shared/models/freight/freight';
import FreightSelected from '../../shared/models/freight/freight-selected';
import QuoteList from '../../shared/models/freight/quote-list';
import InstallmentResponse from '../../shared/models/mercado-pago/installment-response';
import PayerCost from '../../shared/models/mercado-pago/payer-cost';
import Order from '../../shared/models/order/order';
import Card from '../../shared/models/payment/card';
import { Product } from '../../shared/models/product/product';
import { StorageConstants } from '../../shared/models/storage/storage-constants';
import Address from '../../shared/models/user/address';
import User from '../../shared/models/user/user';
import { SharedModule } from '../../shared/shared.module';
import { validCPFOrCNPJ } from '../../shared/utils/document';
import {
  validEmail,
  validName,
  validPhone,
} from '../../shared/utils/validators';
import { UserError } from '../register-user/register-user.component';

export enum PaymentMethodsEnum {
  PIX = 'PIX',
  CREDIT_CARD = 'CARTAO_CREDITO',
}

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    AddressComponent,
    PrimeNGModule,
    CreditCardInputComponent,
    ModalPixComponent,
    SharedModule,
    FreightQuotesDesktopComponent,
  ],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
  providers: [ToastService, MessageService],
})
export class PaymentComponent implements OnInit {
  STATUS = 'status';
  AVAILABLE = 'AVAILABLE';
  ONLINE_DEBIT = 'ONLINE_DEBIT';
  MEDIUM = 'MEDIUM';
  PAYMENTSMETHODS = 'paymentMethods';

  public address: Address = new Address();
  public addressDisable: AddressDisable = new AddressDisable();
  public addressError: AddressError = new AddressError();
  public card: Card = new Card();
  public cardInputError: CardInputError = new CardInputError();
  public cartCheckout: CartCheckoutDetails | undefined;
  public coupon: string | null = null;
  public couponDiscount: CouponDiscount | undefined;
  public loadingFreight = false;
  public order: Order = new Order();
  public paymentSelected: string | undefined = 'PIX';
  public pix = new Pix();
  public quotes: QuoteList | undefined;
  public showModalPix = false;
  public user: User = new User();
  public zipCode?: string;
  public acceptNewsletter = true;
  public sameAddressForInvoice = true;
  public userError: UserError = new UserError();
  public freightStorage?: FreightSelected;
  public budget: Budget = new Budget();
  public installments: InstallmentResponse = new InstallmentResponse();
  public payeCostSelected?: PayerCost;
  public showAdditionalData = false;
  public totalValueWithoutInterest = 0;

  //Budget
  public showName = true;
  public showDocument = true;
  public showEmail = true;

  constructor(
    private activatedRoute: ActivatedRoute,
    private addressService: AddressService,
    private authService: AuthService,
    private budgetServices: BudgetService,
    private cartService: CartService,
    private confirmationService: ConfirmationService,
    private freightService: FreightService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private localStorageService: LocalStorageService,
    private productsService: ProductsService,
    public router: Router,
    private toastService: ToastService,
    private transactionsService: TransactionsService,
    public userService: UserService,
    private loaderService: LoaderService,
    private sessionStorageService: SessionStorageService,
    private mercadoPagoService: MercadoPagoService,
    private utmService: UtmService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.authService.userData$.subscribe((res: any) => {
      this.user = new User();
      if (res) {
        this.user = new User();
        this.user.fromJson(res);
      }
    });
  }

  async ngOnInit() {
    this.configCheckout();
  }

  async configCheckout() {
    try {
      this.loaderService.show();
      this.zipCode = await this.localStorageService.get(
        StorageConstants.RM_ECOMMERCE_ZIP_CODE
      );

      this.freightStorage = await this.sessionStorageService.get(
        StorageConstants.RM_ECOMMERCE_FREIGHT
      );

      const hash =
        this.activatedRoute.snapshot.paramMap.get('hashBudget') || '';

      if (hash) {
        this.budget = await this.budgetServices.get(hash);
        this.cartCheckout = await this.cartService.prepareCheckoutBudget(
          this.budget.hash || ''
        );
        this.user = this.cartCheckout.cart.user || new User();
        this.budget.cart = this.cartCheckout.cart;
        this.budget.cart.discountSeller = this.budget.discountSeller;
        this.configDataBudget();
        this.cartService.setAuxVariables(this.budget.cart, undefined);
      } else if (this.hasUser()) {
        this.cartCheckout = await this.cartService.prepareCheckout({
          ...this.freightStorage,
        });
      } else {
        const cartItems = await this.cartService.getItems(null);
        this.cartCheckout = await this.cartService.prepareCheckoutWithoutUser(
          cartItems,
          { ...this.freightStorage }
        );
      }

      if (this.cartCheckout) {
        if (!this.budget.hash) {
          this.address = await this.addressService.getAddress(
            this.zipCode || ''
          );
        }
        this.addressDisable.address = !!this.address.address;
        this.addressDisable.district = !!this.address.district;
        this.addressDisable.complement = false;
        this.addressDisable.number = false;

        this.googleAnalyticsService.sendEventBeginCheckout(this.cartCheckout);
        this.calculateFreight();
      } else {
        this.userService.goToCart('');
      }
    } catch (error: any) {
      if (this.budget.hash) {
        this.userService.goToBudget(this.budget.hash, error);
      } else {
        this.userService.goToCart(error);
      }
    } finally {
      this.loaderService.hide();
    }
  }

  configDataBudget() {
    this.showName = !validName(this.user?.name || '');
    this.showDocument = !validCPFOrCNPJ(this.user?.document || '');
    this.showEmail = !validEmail(this.user?.email || '');
    this.configAdditionalData();
  }

  configAdditionalData() {
    this.showAdditionalData =
      !this.user?.document ||
      !this.user.email ||
      !this.user?.getFirstAddress()?.numberAddress ||
      !this.user?.getFirstAddress()?.complement;

    if (this.showAdditionalData) {
      this.addressError.errorNumber = true;
      this.addressError.errorComplement = true;
      this.userError.errorEmail = true;
      this.userError.errorDocument = true;
    }
  }

  async calculateFreight() {
    if (this.budget.hash) return;

    this.quotes = undefined;
    if (this.address?.zipCode) {
      try {
        this.loadingFreight = true;
        const calculate: CalculateShipping = new CalculateShipping();
        calculate.cep = this.address?.zipCode;
        calculate.cartId = this.cartCheckout?.cart?.id;
        const quotes = await this.freightService.calculateShippingOrCart(
          calculate
        );

        if (quotes) {
          this.quotes = quotes;
          if ((quotes?.carriers?.length || 0) > 0) {
            let daysOnlyOrder = 0;
            this.cartCheckout?.cart?.cartProducts?.forEach((i) => {
              if ((i?.product?.daysOnlyOrder || 0) > daysOnlyOrder) {
                daysOnlyOrder = i?.product?.daysOnlyOrder || 0;
              }
            });

            if (daysOnlyOrder > 0) {
              this.quotes?.carriers?.forEach((carrier) => {
                if (carrier.optionFreight === Freight.STORE_PICKUP) {
                  carrier.deliveryTime =
                    (carrier.deliveryTime || 0) + daysOnlyOrder;
                }
              });
            }
          } else {
            return this.toastService.error(
              'Para cotar esse frete com 6 metros, solicite via whatsapp!'
            );
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingFreight = false;
      }
    }
  }

  getFinalValue(): number {
    let value = this.cartCheckout?.gross || 0;
    if (this.cartCheckout) {
      const discountAllProduct =
        this.cartCheckout?.gross - this.cartCheckout?.total;

      if (discountAllProduct > 0 && !this.couponDiscount) {
        value -= discountAllProduct;
      }

      if (this.paymentSelected === PaymentMethodsEnum.PIX) {
        if ((this.budget.discountSeller || 0) > 0 && !this.couponDiscount) {
          const discountSeller = this.budget.discountSeller || 0;
          const valueDiscountSeller = (value * discountSeller) / 100;
          value -= valueDiscountSeller;
        }
      }

      if (this.couponDiscount) {
        value = value - (this.couponDiscount?.totalDiscount || 0);
      }

      value += this.getValueFreight();

      const additionalValue = this.budget.additionalValue || 0;
      value += additionalValue > 0 ? additionalValue : 0;

      const availableCredit = this.budget.availableCredit || 0;
      value -= availableCredit > 0 ? availableCredit : 0;

      value += this.getInterest(value);
      this.card.transactionAmount = Number(Number(value).toFixed(2));
    }
    return value;
  }

  getValueFreight() {
    let value = 0;
    if (this.cartCheckout?.freightSelected?.freight) {
      const priceFreight =
        this.cartCheckout?.freightSelected?.freight?.priceFreight || 0;
      value = priceFreight;
    }

    if ((this.budget.valueFreight || 0) > 0) {
      value = this.budget.valueFreight || 0;
    }

    return value;
  }

  getInterest(totalValue: number) {
    let value = 0;
    if ((this.payeCostSelected?.installments || 0) > 4) {
      const interestValue =
        (this.payeCostSelected?.total_amount || 0) - totalValue;
      value = interestValue;
    }

    return value;
  }

  valueProductsWithoutDiscount(): number {
    let valueTotal = 0;
    this.cartCheckout?.cart?.cartProducts?.forEach((cp) => {
      const product = cp?.product || new Product();
      if (cp.discount === 0) {
        const amount = cp?.amount || 0;
        let value = cp?.value || 0;

        if (product.isPersiana()) {
          if (cp.drive === BlindsOptionsEnum.DRIVE_MOTOR) {
            value += this.productsService.getValueMotor();
          }

          if (cp?.panel === BlindsOptionsEnum.WITH_PANEL) {
            value += this.productsService.getValueTotalBando(
              cp.customWidth || 0,
              product.subCategory || ''
            );
          }
        }

        valueTotal += amount * value;
      }
    });
    return valueTotal;
  }

  async createCardToken() {
    const emailInput = document.getElementById(
      'form-checkout__cardholderEmail'
    ) as HTMLInputElement;
    if (emailInput) {
      emailInput.value = this.user.email || '';
    }

    try {
      const token = await this.mercadoPagoService.getCardToken();
      return token;
    } catch (error) {
      console.error('Erro ao gerar token:', error);
      return '';
    }
  }

  clearedZip() {
    this.quotes = undefined;
    if (this.cartCheckout) {
      this.cartCheckout.freightSelected = undefined;
    }
    this.addressError.validAll(true);
  }

  foundAddress(address: Address) {
    if (address?.zipCode?.length === 9) {
      this.calculateFreight();
    }
  }

  async setFreightSelectedDesktop(carrier: Carrying) {
    const freightSelected = new FreightSelected(
      carrier,
      this.quotes?.tokenOffer || ''
    );
    this.setFreightSelected(freightSelected);
  }

  async setFreightSelected(freightSelected: FreightSelected) {
    if (this.cartCheckout) {
      this.cartCheckout.freightSelected = freightSelected;
      this.card = new Card();
      this.clearInstallmentsCombo();
      this.addressError.errorNumber =
        !this.freightIsStorePickup() && !this.address.numberAddress;
      this.addressError.errorComplement =
        !this.freightIsStorePickup() && !this.address.complement;
    }
  }

  freightIsStorePickup(): boolean {
    return (
      this.cartCheckout?.freightSelected?.optionFreight === Freight.STORE_PICKUP
    );
  }

  async onClickBtcalculateCoupon() {
    if (this.coupon) {
      try {
        this.couponDiscount = await this.cartService.coupon(
          this.cartCheckout?.cart?.id || 0,
          this.coupon?.toUpperCase()
        );
        this.coupon = null;
        if (this.couponDiscount) {
          if (
            this.couponDiscount?.coupon?.meanOfPayment === 'IN_CASH' &&
            this.paymentSelected === PaymentMethodsEnum.CREDIT_CARD
          ) {
            this.couponDiscount = undefined;
            this.toastService.error(
              'Cupom disponível apenas para pagamento via PIX!'
            );
          } else {
            this.toastService.success('Cupom aplicado com sucesso!');
          }
        }
      } catch (error: any) {
        this.toastService.error(error || 'Cupom inválido!');
      }
    } else {
      this.toastService.error('Informe um cupom!');
    }
  }

  async onClickCheckout() {
    this.user.birthDate = '01/01/2000';
    if (this.validaUser()) {
      if (this.validCoupon()) {
        if (this.budget.hash) {
          this.checkoutBudget();
        } else {
          this.checkoutNormal();
        }
      }
    } else {
      return this.toastService.error('Informe todos os seus dados!');
    }
  }

  async checkoutBudget() {
    try {
      if (this.showAdditionalData) {
        this.addressError.errorNumber = !this.address?.numberAddress;
        this.addressError.errorComplement = !this.address?.complement;
        this.addressError.errorDistrict = !this.address?.district;
        this.addressError.errorUF = !this.address?.uf;
        this.userError.errorDocument = !this.user.document;
        this.userError.errorEmail = !this.user.email;

        if (
          this.addressError.errorNumber ||
          this.addressError.errorComplement ||
          this.userError.errorDocument ||
          this.userError.errorEmail
        ) {
          this.toastService.error('Por favor, preencha todos os campos!');
          return;
        }
      }

      const payment: CheckoutPayment = await this.populateCheckoutPayment();
      const checkout: CheckoutBudget = new CheckoutBudget(
        this.budget.hash || '',
        payment,
        this.showAdditionalData ? this.address : null,
        this.utmService.getUtmData()
      );

      this.loaderService.show();
      const order = await this.transactionsService.checkoutBudget(checkout);
      this.loaderService.hide();

      if (order) {
        this.responseCheckout(order);
      } else {
        this.toastService.error(
          'Erro ao gerar seu pedido! Entre em contato pelo WhatsApp!'
        );
        this.runInBrowser(() => {
          window.location.reload();
        }, 4000);
      }
    } catch (error: any) {
      this.loaderService.hide();
      this.toastService.error(`Erro ao gerar seu pedido: ${error || ''}`);
      this.runInBrowser(() => {
        window.location.reload();
      }, 4000);
    }
  }

  checkoutNormal() {
    const msgValidation = this.validPayment();
    if (!msgValidation) {
      if (this.freightIsStorePickup()) {
        this.checkout();
      } else {
        this.confirmationService.confirm({
          message: 'O endereço de entrega está correto?',
          header: 'Confirmação',
          accept: async () => {
            this.checkout();
          },
        });
      }
    } else {
      return this.toastService.error(msgValidation);
    }
  }

  validaUser() {
    let hasError = true;
    this.userError.errorName = !validName(this.user.name || '');
    this.userError.errorEmail = !validEmail(this.user.email || '');
    this.userError.errorDocument = !validCPFOrCNPJ(this.user.document || '');
    this.userError.errorPhone = !validPhone(this.user.phone || '');

    hasError =
      this.userError.errorName ||
      this.userError.errorEmail ||
      this.userError.errorDocument ||
      this.userError.errorPhone;

    return !hasError;
  }

  async checkout() {
    try {
      const payment: CheckoutPayment = await this.populateCheckoutPayment();

      if (!this.hasUser()) {
        this.user.address = [];
        this.user.address?.push(this.address);
      }

      const address = this.freightIsStorePickup()
        ? this.user.getAddress(true)
        : this.address;

      const checkout: Checkout = new Checkout(
        this.cartCheckout?.cart?.id,
        this.cartCheckout?.freightSelected,
        payment,
        address,
        this.sameAddressForInvoice,
        this.utmService.getUtmData()
      );

      if (this.couponDiscount?.coupon) {
        checkout.coupon = this.couponDiscount.coupon;
      }

      this.loaderService.show();
      let order;
      if (this.hasUser()) {
        order = await this.transactionsService.checkout(checkout);
      } else {
        order = await this.transactionsService.checkoutSimplified(checkout);
      }
      this.loaderService.hide();

      if (order) {
        this.responseCheckout(order);
      } else {
        this.toastService.error(
          'Erro ao gerar seu pedido! Entre em contato pelo WhatsApp!'
        );
        this.runInBrowser(() => {
          window.location.reload();
        }, 4000);
      }
    } catch (error: any) {
      this.loaderService.hide();
      this.toastService.error(`Erro ao gerar seu pedido: ${error || ''}`);
      this.runInBrowser(() => {
        window.location.reload();
      }, 4000);
    }
  }

  private runInBrowser(callback: () => void, delay = 0): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(callback, delay);
    }
  }

  async responseCheckout(order: Order) {
    this.sendEventsToGoogleAnalytics(order);
    this.order = order;
    await this.cartService.removeCartLocalStorage();
    this.cartService.updateCartState(true, false);
    if (this.paymentSelected === PaymentMethodsEnum.CREDIT_CARD) {
      this.userService.goToOrderPlaced(this.order);
    } else if (this.paymentSelected === PaymentMethodsEnum.PIX) {
      this.pix.pixData = this.order?.orderTransaction?.pix;
      this.pix.base64QrCode = this.order?.orderTransaction?.base64QrCode;
      this.pix.value = this.order.totalValue;
      this.showModalPix = true;
    }
  }

  async populateCheckoutPayment(): Promise<CheckoutPayment> {
    let crediCart: CreditCart | null = null;
    if (this.paymentSelected === PaymentMethodsEnum.CREDIT_CARD) {
      const cardFormat: Card = { ...this.card };
      cardFormat.cardNumber = cardFormat?.cardNumber?.replace(/\D/g, '');
      cardFormat.expirationDate = cardFormat?.expirationDate;
      cardFormat.token = await this.createCardToken();
      cardFormat.installments = cardFormat.installments;
      crediCart = new CreditCart(this.user, cardFormat);
    }

    const sender: PaymentSender = new PaymentSender(
      this.user,
      crediCart?.token || '',
      this.acceptNewsletter
    );

    const payment: CheckoutPayment = new CheckoutPayment(
      crediCart,
      this.paymentSelected,
      sender,
      null
    );

    return payment;
  }

  sendEventsToGoogleAnalytics(order: Order) {
    this.googleAnalyticsService.sendEventPurcharse(order);
    this.googleAnalyticsService.sendEventOrderPlaced(order);
    if (this.cartCheckout) {
      this.googleAnalyticsService.sendEventAddPaymentInfo(
        this.cartCheckout,
        this.paymentSelected === PaymentMethodsEnum.CREDIT_CARD
          ? 'Cartão de Crédito'
          : 'PIX'
      );

      if (this.cartCheckout?.freightSelected) {
        this.googleAnalyticsService.sendEventAddShippingInfo(
          this.cartCheckout,
          this.cartCheckout?.freightSelected,
          this.zipCode || ''
        );
      }
    }
  }

  selectPayment(selectedValue: string): void {
    this.card = new Card();
    this.paymentSelected = selectedValue;

    if (this.paymentSelected === PaymentMethodsEnum.CREDIT_CARD) {
      this.couponDiscount = undefined;
      const value = this.getFinalValue();
      this.totalValueWithoutInterest = value;
      this.mercadoPagoService.loadCardFormWithIframe(value);
    }

    if (this.paymentSelected === PaymentMethodsEnum.PIX) {
      this.payeCostSelected = undefined;
      this.clearInstallmentsCombo();
    }
  }

  clearInstallmentsCombo(): void {
    const selectElement = document.getElementById(
      'form-checkout__installments'
    ) as HTMLSelectElement;

    if (!selectElement) return;

    selectElement.innerHTML = ''; // Remove todas as opções

    const placeholderOption = document.createElement('option');
    placeholderOption.disabled = true;
    placeholderOption.selected = true;
    placeholderOption.textContent = 'Número de parcelas';
    selectElement.appendChild(placeholderOption);
  }

  validCoupon() {
    if (this.couponDiscount) {
      if (this.paymentSelected === PaymentMethodsEnum.CREDIT_CARD) {
        this.toastService.error(
          'Cupom disponível apenas para pagamento via PIX. Remova o cupom para realizar o pagamento com cartão de crédito!',
          5000
        );
        return false;
      }
    }
    return true;
  }

  validPayment() {
    if (
      !this.cartCheckout?.freightSelected ||
      (this.cartCheckout?.freightSelected &&
        this.cartCheckout?.freightSelected?.optionFreight ===
          Freight.CARRYING &&
        !this.cartCheckout?.freightSelected?.freight)
    ) {
      return 'Selecione um frete!';
    }

    if (!this.freightIsStorePickup()) {
      if (!this.address?.numberAddress || !this.address?.complement) {
        if (!this.address?.numberAddress) {
          this.addressError.errorNumber = true;
        }
        if (!this.address?.complement) {
          this.addressError.errorComplement = true;
        }
        return 'Informe os dados de entrega corretamente!';
      } else {
        this.addressError.errorNumber = false;
        this.addressError.errorComplement = false;
      }
    }

    if (!this.paymentSelected) {
      return 'Selecione uma forma de pagamento!';
    }

    if (this.paymentSelected === PaymentMethodsEnum.CREDIT_CARD) {
      this.cardInputError.errorCardNumber =
        (this.card?.cardNumber?.length || 0) < 16;
      this.cardInputError.errorCvv = (this.card?.cvv?.length || 0) < 3;
      this.cardInputError.errorExpirationDate =
        (this.card?.expirationDate?.length || 0) < 7;
      this.cardInputError.errorName = (this.card?.name?.length || 0) < 5;
      this.cardInputError.errorInstallments = !this.card.installments;
      this.cardInputError.errorDocument = !validCPFOrCNPJ(
        this.card.document || ''
      );

      const hasError =
        this.cardInputError.errorCardNumber ||
        this.cardInputError.errorCvv ||
        this.cardInputError.errorExpirationDate ||
        this.cardInputError.errorName ||
        this.cardInputError.errorInstallments ||
        this.cardInputError.errorDocument;

      return hasError
        ? 'Preencha corretamente os dados do seu cartáo de crédito!'
        : null;
    }
    return null;
  }

  haveProductDiscount(): boolean {
    if (this.cartCheckout) {
      return this.cartCheckout.gross - this.cartCheckout.total > 0;
    }
    return false;
  }

  haveSellerDiscount(): boolean {
    if (this.budget.hash) {
      return (this.budget.discountSeller || 0) > 0;
    }
    return false;
  }

  async onClickBtRemoveCoupon() {
    try {
      this.couponDiscount = undefined;
      this.card = new Card();
      this.toastService.success('Cupom removido com sucesso!');
    } catch (error: any) {
      this.toastService.error(
        'Erro',
        error?.message || 'Erro ao remover o cupom!'
      );
    }
  }

  closeModalPix() {
    this.userService.goToOrderPlaced(this.order);
  }

  async onCompleteDocument() {
    const document = this.user?.document?.replace(/\D/g, '') || '';
    if (!validCPFOrCNPJ(document)) {
      this.toastService.error(
        (this.user?.isCompany ? 'CNPJ' : 'CPF') + ' digitado é inválido!'
      );
    }
  }

  hasUser() {
    return !!this.user?.id;
  }

  getTotalWeight() {
    let total = 0;

    if (this.cartCheckout) {
      this.cartCheckout?.cart?.cartProducts?.forEach((item: CartItem) => {
        total += item?.product?.weight || 0;
      });
    }
    return total.toFixed(2);
  }

  valueDiscountSeller() {
    if (this.budget.discountSeller === 0) return;
  }
}
